import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError, api } from "@/api/client";
import { useMe } from "@/api/hooks";
import { useAiStatus, type Draft, type Score, type Source, type Summary } from "@/api/service";
import { Badge } from "@/components/crm/Badge";
import { Icon } from "@/components/icons";
import { Alert } from "@/components/ui";
import { can } from "@/lib/permissions";

const LINK: Record<string, (id: string) => string> = {
  deal: (id) => `/deals/${id}`, account: (id) => `/accounts/${id}`, contact: (id) => `/contacts/${id}`, quote: (id) => `/quotes/${id}`,
  order: (id) => `/orders/${id}`, ticket: (id) => `/service/${id}`, conversation: (id) => `/inbox?c=${id}`,
};
const message = (e: unknown) => (e instanceof ApiError ? e.message : "The AI could not answer; try again");

/** True when this person may use AI and the workspace has it on. */
export function useAi(): boolean {
  const perms = useMe().data?.permissions ?? [];
  const status = useAiStatus();
  return can(perms, "ai:use") && !!status.data?.available;
}

export function Sources({ sources }: { sources: Source[] }) {
  if (!sources.length) return null;
  return (
    <p className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-muted" aria-label="Sources">
      <span>Based on</span>
      {sources.slice(0, 8).map((s) => (
        <Link key={s.id} to={LINK[s.type]?.(s.id) ?? "#"} className="rounded-full border border-line px-2 py-0.5 hover:border-brand hover:text-brand">{s.label}</Link>
      ))}
    </p>
  );
}

/** "Summarise with AI": a short brief, next steps and the records it used. */
export function AiSummary({ objectType, id }: { objectType: "deal" | "account" | "contact" | "ticket" | "conversation"; id: string }) {
  const enabled = useAi();
  const [data, setData] = useState<Summary | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!enabled) return null;
  const run = async () => {
    setBusy(true); setError(null);
    try { setData(await api<Summary>("POST", "/ai/summary", { object_type: objectType, id })); } catch (e) { setError(message(e)); } finally { setBusy(false); }
  };
  return (
    <section className="card border-brand/30 p-5" aria-label="AI summary">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-semibold"><Icon name="zap" className="h-4 w-4 text-brand" />AI summary</h2>
        <button type="button" className="btn-ghost text-xs" disabled={busy} onClick={() => void run()}>{busy ? "Reading the history…" : data ? "Refresh" : "Summarise"}</button>
      </div>
      {error && <div className="mt-2"><Alert>{error}</Alert></div>}
      {!data && !busy && !error && <p className="mt-1 text-sm text-muted">A short brief of where this stands, with next steps, from its history.</p>}
      {data && <>
        <p className="mt-2 text-sm">{data.summary}</p>
        {data.next_steps.length > 0 && <>
          <h3 className="mt-3 text-xs font-semibold uppercase tracking-wide text-muted">Next steps</h3>
          <ol className="mt-1 list-inside list-decimal space-y-0.5 text-sm">{data.next_steps.map((s) => <li key={s}>{s}</li>)}</ol>
        </>}
        <p className="mt-2 text-xs text-muted">Confidence: {data.confidence}. Check before acting; the AI only read this record's history.</p>
        <Sources sources={data.sources} />
      </>}
    </section>
  );
}

/** A button that asks for a draft and hands it to the caller; nothing is sent. */
export function DraftButton({ body, onDraft, label = "Draft with AI" }: { body: Record<string, unknown>; onDraft: (d: Draft) => void; label?: string }) {
  const enabled = useAi();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!enabled) return null;
  return (
    <span className="inline-flex flex-col items-start">
      <button type="button" className="btn-ghost border border-brand/40 text-xs text-brand" disabled={busy}
        onClick={() => { setBusy(true); setError(null); api<Draft>("POST", "/ai/draft", body).then(onDraft).catch((e) => setError(message(e))).finally(() => setBusy(false)); }}>
        <Icon name="zap" className="h-3.5 w-3.5" />{busy ? "Drafting…" : label}
      </button>
      {error && <span className="mt-1 text-xs text-danger" role="alert">{error}</span>}
    </span>
  );
}

const BAND_TONE = { hot: "danger", warm: "warn", cold: "neutral" } as const;
export const band = (score: number) => (score >= 70 ? "hot" : score >= 40 ? "warm" : "cold");

/** The deal's score with its top reasons; recalculates on request. */
export function ScoreCard({ dealId, score, reasons }: { dealId: string; score: number | null | undefined; reasons: string[] | undefined }) {
  const [current, setCurrent] = useState<{ score: number | null | undefined; reasons: string[] }>({ score, reasons: reasons ?? [] });
  const [busy, setBusy] = useState(false);
  const refresh = () => { setBusy(true); api<Score>("POST", `/deals/${dealId}/score`).then((s) => setCurrent({ score: s.score, reasons: s.reasons })).finally(() => setBusy(false)); };
  return (
    <section className="card p-5" aria-label="Deal score">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">Score</h2>
        <button type="button" className="btn-ghost text-xs" disabled={busy} onClick={refresh}>{busy ? "Scoring…" : "Recalculate"}</button>
      </div>
      {current.score == null ? <p className="mt-1 text-sm text-muted">Not scored yet. Scores refresh every few hours.</p> : <>
        <p className="mt-1 flex items-center gap-2"><span className="text-3xl font-semibold tabular-nums" data-testid="deal-score">{current.score}</span>
          <Badge tone={BAND_TONE[band(current.score)]}>{band(current.score)}</Badge></p>
        <ul className="mt-2 space-y-1 text-sm">
          {current.reasons.map((r) => <li key={r} className="flex gap-2"><span className={r.startsWith("+") ? "text-ok" : "text-danger"} aria-hidden="true">{r.startsWith("+") ? "▲" : "▼"}</span>{r.slice(2)}</li>)}
        </ul>
        <p className="mt-2 text-xs text-muted">Learned from your own won and lost deals.</p>
      </>}
    </section>
  );
}

export function ScoreChip({ score }: { score: number | null | undefined }) {
  if (score == null) return null;
  const b = band(score);
  return <span className={`rounded px-1.5 text-[11px] font-semibold tabular-nums ${b === "hot" ? "bg-danger/10 text-danger" : b === "warm" ? "bg-warn/10 text-warn" : "bg-canvas text-muted"}`} title={`Score ${score} (${b})`}>{score}</span>;
}
